import { useCallback, useEffect, useRef, useState } from "react";
import { logout } from "../services/authService";
import { Offcanvas } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isSuperAdminProjectSession, getSessionProjectName, getProjectPid } from "../utils/sessionProject";
import {
  BookOpen,
  Bot,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  DollarSign,
  FileText,
  FolderKanban,
  History,
  Home,
  List,
  Menu,
  Mic,
  LogOut,
  Plus,
  Settings,
  TicketCheck,
  UserRound,
  Users,
  Zap,
} from "lucide-react";
import { images } from "../assets/images";
import "../assets/css/Header.css";

const superAdminMenuRoutes = [
  "/super-admin-dashboard",
  "/project-list",
  "/project-add",
  "/usage-history",
  "/ai-model-pricing",
  "/admin-invoices",
  "/settings",
  "/support-ticket",
  "/support-ticket-chat",
];

const companyAdminMenuRoutes = [
  "/dashboard",
  "/user-list",
  "/knowledge-base",
  "/settings",
  "/prompt-history",
  "/ask-ai",
  "/ask-ai/text",
  "/ask-ai/voice",
  "/form-builder",
  "/ai-agent-settings",
  "/prompts",
  "/url-list",
  "/url-settings",
  "/support-ticket",
  "/project-admin-usage",
];

const pageTitles = {
  "/super-admin-dashboard": "Super Admin Dashboard",
  "/dashboard": "Dashboard",
  "/profile": "Profile",
  "/project-list": "Projects",
  "/project-add": "Add Project",
  "/project-details": "Project Details",
  "/usage-history": "Usage History",
  "/usage-history-details": "Usage History Details",
  "/ai-model-pricing": "AI Model Pricing",
  "/admin-invoices": "Invoices",
  "/settings": "Settings",
  "/user-list": "Users",
  "/user-details": "User Details",
  "/knowledge-base": "Knowledge Base",
  "/knowledge-base-details": "Knowledge Base",
  "/prompt-history": "History",
  "/prompt-history-details": "History",
  "/ask-ai": "Ask AI",
  "/ask-ai/text": "Text AI Chat",
  "/ask-ai/voice": "Voice AI Chat",
  "/form-builder": "Form Builder",
  "/ai-agent-settings": "AI Agent",
  "/prompts": "Prompts",
  "/url-list": "Urls",
  "/url-settings": "Url Settings",
  "/support-ticket": "Support Ticket",
  "/support-ticket-chat": "Support Ticket",
  "/project-admin-usage": "Usage Details",
};

// Super-admin navigation for the single dashboard page
const mainLinks = [
  {
    to: "/super-admin-dashboard",
    label: "Dashboard",
    icon: Home,
    activePaths: ["/super-admin-dashboard"],
  },
];

const companiesGroup = {
  key: "companies",
  label: "Projects",
  icon: FolderKanban,
  items: [
    {
      to: "/project-list",
      label: "View Projects",
      icon: List,
      activePaths: ["/project-list", "/project-details"],
    },
    {
      to: "/project-add",
      label: "Add Project",
      icon: Plus,
      activePaths: ["/project-add"],
    },
  ],
};

const mainLinksBottom = [
  {
    to: "/usage-history",
    label: "Usage History",
    icon: History,
    activePaths: ["/usage-history", "/usage-history-details"],
  },
  {
    to: "/ai-model-pricing",
    label: "AI Model Pricing",
    icon: DollarSign,
    activePaths: ["/ai-model-pricing"],
  },
  {
    to: "/admin-invoices",
    label: "Invoices",
    icon: FileText,
    activePaths: ["/admin-invoices"],
  },
  {
    to: "/admin-support-ticket",
    label: "Support Ticket",
    icon: TicketCheck,
    activePaths: ["/admin-support-ticket", "/admin-support-ticket-chat"],
  },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    activePaths: ["/settings"],
  },
];

// Company admin links
const companyAdminMainLinks = [
  { to: "/dashboard", label: "Dashboard", icon: Home, activePaths: ["/dashboard"] },
  { to: "/user-list", label: "Users", icon: Users, activePaths: ["/user-list", "/user-details"] },
  { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, activePaths: ["/knowledge-base", "/knowledge-base-details"] },
];

const companyAdminSettingsGroup = {
  key: "settings",
  label: "Settings",
  icon: Settings,
  items: [
    { to: "/prompts", label: "Prompts", icon: ClipboardList, activePaths: ["/prompts"] },
    { to: "/url-list", label: "Urls", icon: List, activePaths: ["/url-list", "/url-settings"] },
  ],
};

const companyAdminAskAiGroup = {
  key: "ask-ai",
  label: "Ask AI",
  icon: Bot,
  items: [
    { to: "/ask-ai/text", label: "Text AI Chat", icon: ClipboardList, activePaths: ["/ask-ai/text"] },
    { to: "/ask-ai/voice", label: "Voice AI Chat", icon: Mic, activePaths: ["/ask-ai/voice"] },
  ],
};

// Themed fallback avatar — replaces the hardcoded-blue raster image when no
// profile picture is set (or it fails to load), so it follows --primaryColor
// like the rest of the UI instead of a fixed-color image asset.
// Icon glyphs (UserRound etc) are visually top-heavy in a circle — the dense
// closed head-circle vs. the thin open shoulder-arc reads as off-center no
// matter how precisely it's geometrically centered, and the exact offset
// needed to compensate shifts depending on the parent's border/overflow
// (compare the borderless header pill vs. the bordered .hpd-avatar). Text
// centers cleanly via flex regardless of context, so use initials instead.
const ProfileAvatar = ({ src, name = "", size = 42 }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);

  if (src && !failed) {
    return <img src={src} alt="user-img" onError={() => setFailed(true)} />;
  }

  const initials = name.trim()
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : null;

  return (
    <div style={{
      width: "100%", height: "100%", minWidth: size, minHeight: size,
      borderRadius: "50%",
      background: "color-mix(in srgb, var(--primaryColor) 12%, #fff)",
      color: "var(--primaryColor)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>
      {initials ? (
        <span style={{ fontSize: Math.round(size * 0.4), fontWeight: 700, lineHeight: 1 }}>
          {initials}
        </span>
      ) : (
        <UserRound size={Math.round(size * 0.55)} style={{ display: "block", flexShrink: 0 }} />
      )}
    </div>
  );
};

const companyAdminBottomLinks = [
  { to: "/prompt-history", label: "History", icon: History, activePaths: ["/prompt-history", "/prompt-history-details"] },
  { to: "/project-admin-usage", label: "Usage Details", icon: Zap, activePaths: ["/project-admin-usage"] },
  { to: "/support-ticket", label: "Support Ticket", icon: TicketCheck, activePaths: ["/support-ticket"] },
  // { to: "/form-builder", label: "Form Builder", icon: ClipboardList, activePaths: ["/form-builder"] },
];


const getPageTitle = (pathname) => {
  const currentPath = pathname.toLowerCase();
  const matchedTitle = Object.keys(pageTitles)
    .sort((a, b) => b.length - a.length)
    .find((route) => currentPath.includes(route));
  return matchedTitle ? pageTitles[matchedTitle] : "Dashboard";
};

const SidebarContent = ({ setShow, navigate, location, isMobile, role, profileName, profileEmail }) => {
  const isLinkActive = (activePaths) =>
    activePaths.some((path) => location.pathname.toLowerCase().includes(path));

  const isGroupActive = (group) =>
    group.items.some((item) => isLinkActive(item.activePaths));

  const [openGroups, setOpenGroups] = useState(() => {
    const open = new Set();
    if (isGroupActive(companiesGroup)) open.add(companiesGroup.key);
    if (isGroupActive(companyAdminSettingsGroup)) open.add(companyAdminSettingsGroup.key);
    if (isGroupActive(companyAdminAskAiGroup)) open.add(companyAdminAskAiGroup.key);
    return open;
  });

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const renderLink = ({ to, label, icon: Icon, activePaths }) => {
    const isActive = isLinkActive(activePaths);
    return (
      <Link
        key={to}
        to={to}
        className={`nav-link option-list ${isActive ? "active-option" : ""}`}
        onClick={() => setShow(false)}
      >
        <div className={`sidebar-icon ${isActive ? "active-option-icon" : ""}`}>
          <Icon size={18} />
        </div>
        <span>{label}</span>
        <ChevronRight className="sidebar-arrow" size={16} strokeWidth={2.2} />
      </Link>
    );
  };

  const renderGroup = (group) => {
    const isOpen = openGroups.has(group.key);
    const isActive = isGroupActive(group);
    const GroupIcon = group.icon;

    return (
      <div key={group.key} className="sidebar-group">
        <button
          className={`sidebar-group-toggle ${isActive ? "active-option" : ""}`}
          onClick={() => toggleGroup(group.key)}
        >
          <div
            className={`sidebar-icon ${isActive ? "active-option-icon" : ""}`}
          >
            <GroupIcon size={18} />
          </div>
          <span>{group.label}</span>
          {isOpen ? (
            <ChevronUp size={15} className="sidebar-group-chevron" />
          ) : (
            <ChevronDown size={15} className="sidebar-group-chevron" />
          )}
        </button>

        {isOpen && (
          <div className="sidebar-submenu">
            {group.items.map(({ to, label, icon: Icon, activePaths }) => {
              const isActive = isLinkActive(activePaths);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`sidebar-submenu-item ${isActive ? "submenu-item-active" : ""}`}
                  onClick={() => setShow(false)}
                >
                  <div
                    className={`sidebar-sub-icon ${isActive ? "active-option-icon" : ""}`}
                  >
                    <Icon size={15} />
                  </div>
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        <div
          className="sidebar-profile cursor-pointer"
          onClick={() => {
            navigate("/profile");
            setShow(false);
          }}
        >
          <div className="menu-profile-image">
            <ProfileAvatar name={profileName} />
          </div>
          <div className="min-w-0">
            <h6 className="user-name-text mb-0 text-capitalize">
              {profileName}
            </h6>
            <p className="user-email mb-0 text-truncate">{profileEmail}</p>
          </div>
        </div>
      ) : (
        <div className="sideBarUserDetails shadow-bottom d-flex align-items-center justify-content-center">
          <div className="text-center w-100">
            <Link to={role === 'project_admin' ? "/dashboard" : "/super-admin-dashboard"} onClick={() => setShow(false)}>
              <img
                src={images.logoHorizontal}
                alt="logo"
                className="sidebar-brand-logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = images.appLogo;
                }}
              />
            </Link>
          </div>
        </div>
      )}

      <div className="sidebar-list">
        <div className="sidebar-menu-scroll">

          {isSuperAdminProjectSession() && (
            <div style={{
              margin: "12px 12px 4px",
              background: "linear-gradient(135deg, var(--primaryTint) 0%, var(--primaryTintBorder) 100%)",
              border: "1px solid var(--primaryTintBorder)",
              borderLeft: "3px solid var(--primaryColor)",
              borderRadius: 10,
              padding: "10px 12px",
            }}>
              <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, color: "var(--primaryColor)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Super Admin View
              </p>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "var(--secondaryColor)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {getSessionProjectName() || getProjectPid() || "Project"}
              </p>
            </div>
          )}

          <p className="sidebar-menu-title">Main</p>

          {(role === 'project_admin' || isSuperAdminProjectSession()) ? (
            <>
              {companyAdminMainLinks.map(renderLink)}
              {renderGroup(companyAdminSettingsGroup)}
              {renderLink(companyAdminBottomLinks[0])}
              {renderGroup(companyAdminAskAiGroup)}
              {companyAdminBottomLinks.slice(1).map(renderLink)}
            </>
          ) : (
            <>
              {mainLinks.map(renderLink)}
              {renderGroup(companiesGroup)}
              {mainLinksBottom.map(renderLink)}
            </>
          )}

          {isMobile && (
            <Link
              to="/profile"
              className={`nav-link option-list ${location.pathname === "/profile" ? "active-option" : ""}`}
              onClick={() => setShow(false)}
            >
              <div
                className={`sidebar-icon ${location.pathname === "/profile" ? "active-option-icon" : ""}`}
              >
                <UserRound size={18} strokeWidth={2.2} />
              </div>
              <span>Profile</span>
              <ChevronRight
                className="sidebar-arrow"
                size={16}
                strokeWidth={2.2}
              />
            </Link>
          )}
        </div>

        {isSuperAdminProjectSession() && (
          <div style={{ padding: "10px 12px 14px", borderTop: "1px solid var(--borderColor)" }}>
            <button
              onClick={() => window.close()}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", cursor: "pointer",
                background: "linear-gradient(135deg, var(--primaryTint) 0%, var(--primaryTintBorder) 100%)",
                border: "1px solid var(--primaryTintBorder)", borderRadius: 10,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--primaryTintBorder)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "linear-gradient(135deg, var(--primaryTint) 0%, var(--primaryTintBorder) 100%)"}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: "var(--primaryColor)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <LogOut size={14} />
              </div>
              <div style={{ textAlign: "left", minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "var(--primaryColor)" }}>Exit Super Admin View</p>
                <p style={{ margin: 0, fontSize: 10, color: "var(--primaryLight)" }}>Close this tab</p>
              </div>
            </button>
          </div>
        )}

      </div>
    </>
  );
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const role = localStorage.getItem('role');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [show, setShow] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const dropdownRef = useRef(null);
  const profileImageObjectUrlRef = useRef(null);

  const loadHeaderProfileImage = useCallback((profileImageUrl) => {
    const token = localStorage.getItem("token");
    if (profileImageObjectUrlRef.current) {
      URL.revokeObjectURL(profileImageObjectUrlRef.current);
      profileImageObjectUrlRef.current = null;
    }
    if (!token || !profileImageUrl) {
      setProfileImage("");
      return;
    }
    fetch(`${process.env.REACT_APP_BASE_URL}${profileImageUrl}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!res.ok) throw new Error("no picture");
      return res.blob();
    }).then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      profileImageObjectUrlRef.current = objectUrl;
      setProfileImage(objectUrl);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${process.env.REACT_APP_BASE_URL}/api/v1/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(data => {
      const u = data?.response;
      if (u) {
        setProfileName(`${u.firstName || ""} ${u.lastName || ""}`.trim());
        setProfileEmail(u.email || "");
        loadHeaderProfileImage(u.profileImageUrl);
      }
    }).catch(() => {});

    return () => {
      if (profileImageObjectUrlRef.current) URL.revokeObjectURL(profileImageObjectUrlRef.current);
    };
  }, [loadHeaderProfileImage]);

  useEffect(() => {
    const handleProfileImageUpdated = (e) => {
      loadHeaderProfileImage(e.detail?.profileImageUrl);
    };
    window.addEventListener("profile-image-updated", handleProfileImageUpdated);
    return () => window.removeEventListener("profile-image-updated", handleProfileImageUpdated);
  }, [loadHeaderProfileImage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const pageTitle = getPageTitle(location.pathname);

  const urlPid = new URLSearchParams(location.search).get("pid");
  const sessionPid = sessionStorage.getItem('sessionProjectPid');
  const effectiveRole = (urlPid || sessionPid) ? 'project_admin' : role;

  const menuRoutes = effectiveRole === 'project_admin' ? companyAdminMenuRoutes : superAdminMenuRoutes;
  const isMenuRoute = menuRoutes.some(
    (path) => currentPath.toLowerCase().startsWith(path),
  );

  return (
    <>
      {location.pathname !== "/chats" && (
        <nav className="navbar fixed-top header-part">
          <div className="nav-link navbar-brand text-black d-flex gap-1 align-items-center w-100 p-0 justify-content-between me-2">
            <div className="d-flex align-items-center">
              <button
                className="d-lg-none btn p-0 me-2 sidebar-hamburger"
                onClick={() => setShow(true)}
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
              {currentPath !== "/dashboard" && currentPath !== "/super-admin-dashboard" && (
                <Link
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(-1);
                  }}
                  className="nav-link navigation-icon"
                >
                  <i className="fa-solid fa-angle-left text-primary"></i>
                </Link>
              )}
              <h5 className="header-text mb-0 text-start">{pageTitle}</h5>
            </div>

            <div
              className="d-flex align-items-center gap-2 position-relative"
              ref={dropdownRef}
            >
              <div
                className={`${isMobile ? "header-profile-mobile" : "header-profile-pill"} d-flex gap-2 align-items-center cursor-pointer`}
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                <div className="menu-profile-image">
                  <ProfileAvatar src={profileImage} name={profileName} />
                </div>
                <span className="d-none d-lg-inline">{profileName}</span>
              </div>

              {showDropdown && (
                <div className="hpd-card">
                  <div className="hpd-user-row">
                    <div className="hpd-avatar">
                      <ProfileAvatar src={profileImage} name={profileName} size={40} />
                    </div>
                    <div className="hpd-user-info">
                      <p className="hpd-name">{profileName}</p>
                      <p className="hpd-email">{profileEmail}</p>
                    </div>
                  </div>
                  <div className="hpd-divider" />
                  <Link
                    to="/profile"
                    className="hpd-item"
                    onClick={() => setShowDropdown(false)}
                  >
                    <div className="hpd-item-icon">
                      <UserRound size={15} />
                    </div>
                    My Profile
                  </Link>
                  <button
                    className="hpd-item hpd-item-danger"
                    onClick={async () => {
                      setShowDropdown(false);
                      try { await logout(); } catch {}
                      localStorage.clear();
                      navigate("/login");
                    }}
                  >
                    <div className="hpd-item-icon hpd-item-icon-danger">
                      <LogOut size={15} />
                    </div>
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      )}

      <div className="sidebar-desktop sidebar-background">
        <SidebarContent
          setShow={setShow}
          navigate={navigate}
          location={location}
          isMobile={isMobile}
          role={effectiveRole}
          profileName={profileName}
          profileEmail={profileEmail}
        />
      </div>

      <Offcanvas
        show={show}
        onHide={() => setShow(false)}
        placement="start"
        className="p-0 text-black shadow-lg sidebar-background"
        style={{ width: "240px" }}
      >
        <Offcanvas.Body className="p-0">
          <SidebarContent
            setShow={setShow}
            navigate={navigate}
            location={location}
            isMobile={isMobile}
            role={effectiveRole}
            profileName={profileName}
            profileEmail={profileEmail}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default Header;
